/** SA-1080: what saving a routing policy will change, line by line (spec
 *  §5.2). Both documents are the server's normalized form. Pure. */
import { outcomeLabel } from '@/components/process/stepLiveText'
import { canonical, type PolicyDoc } from './routerPolicyModel'
import { BUILTIN_TITLES, CONDITION_LABELS, approvalWords, changeLines as L, ruleKindWords } from './routerPolicyText'

export interface ChangeNames { handler: (id: string) => string; workflow: (id: string) => string }

type Rec = Record<string, unknown>
const rec = (v: unknown): Rec => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : {})
const same = (a: unknown, b: unknown): boolean => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
const str = (v: unknown): string => (v === null || v === undefined ? 'none' : String(v))

function outcomesWords(v: unknown): string {
  const list = Array.isArray(v) ? v.map(String) : []
  return list.length ? list.map((o) => outcomeLabel(o) ?? o).join(', ') : 'none'
}

/** The per-entry lines a kind and a built-in kind share, in the spec's order. */
function entryLines(
  before: Rec, after: Rec, names: ChangeNames,
  field: (name: string, from: string, to: string) => string, changed: (name: string) => string,
): string[] {
  const out: string[] = []
  if (!same(before.label, after.label)) out.push(field('label', str(before.label), str(after.label)))
  if (!same(before.description, after.description)) out.push(changed('description'))
  if (before.handler !== after.handler) out.push(field('handler', names.handler(str(before.handler)), names.handler(str(after.handler))))
  const bc = rec(before.handler_config), ac = rec(after.handler_config)
  if (!same(bc, ac)) {
    const keys = new Set([...Object.keys(bc), ...Object.keys(ac)])
    for (const key of [...keys].sort()) {
      if (same(bc[key], ac[key])) continue
      if (key === 'workflow_id') {
        out.push(field('workflow', bc[key] ? names.workflow(String(bc[key])) : 'none', ac[key] ? names.workflow(String(ac[key])) : 'none'))
      } else if (key === 'outcomes') {
        out.push(field('outcomes', outcomesWords(bc[key]), outcomesWords(ac[key])))
      } else out.push(changed(`handler setting ${key}`))
    }
  }
  if (!same(before.min_confidence, after.min_confidence)) out.push(field('threshold', str(before.min_confidence), str(after.min_confidence)))
  if (!same(before.approve, after.approve)) out.push(field('approval', approvalWords(before.approve), approvalWords(after.approve)))
  if (before.status !== after.status) out.push(field('status', str(before.status), str(after.status)))
  if (!same(before.reply_kind ?? null, after.reply_kind ?? null)) out.push(field('reply kind', str(before.reply_kind), str(after.reply_kind)))
  return out
}

const conditionLabels = (when: Rec): string[] => Object.keys(when).map((k) => CONDITION_LABELS[k] ?? k)

export function policyChanges(before: PolicyDoc, after: PolicyDoc, names: ChangeNames): string[] {
  const out: string[] = []
  if (before.approval_default !== after.approval_default) out.push(L.approvalDefault(before.approval_default, after.approval_default))

  // kinds
  const bk = rec(before.kinds), ak = rec(after.kinds)
  for (const id of Object.keys(ak)) {
    if (id in bk) continue
    const k = rec(ak[id])
    out.push(L.addsKind(id, str(k.label), names.handler(str(k.handler))))
  }
  for (const id of Object.keys(bk)) if (!(id in ak)) out.push(L.removesKind(id, str(rec(bk[id]).label)))
  const kept = Object.keys(bk).filter((id) => id in ak)
  for (const id of kept) {
    out.push(...entryLines(rec(bk[id]), rec(ak[id]), names,
      (f, a, b) => L.kindField(id, f, a, b), (f) => L.kindChanged(id, f)))
  }
  const keptBefore = kept
  const keptAfter = Object.keys(ak).filter((id) => id in bk)
  if (!same(keptBefore, keptAfter)) out.push(L.kindsReordered)

  // built-in kinds
  const bb = rec(before.builtin), ab = rec(after.builtin)
  for (const id of ['internal', 'automated']) {
    const title = BUILTIN_TITLES[id]
    out.push(...entryLines(rec(bb[id]), rec(ab[id]), names,
      (f, a, b) => L.builtinField(title, f, a, b), (f) => L.builtinChanged(title, f)))
  }

  // rules
  const br = (Array.isArray(before.rules) ? before.rules : []).map(rec)
  const ar = (Array.isArray(after.rules) ? after.rules : []).map(rec)
  const bById = new Map(br.map((r) => [String(r.id), r]))
  const aById = new Map(ar.map((r) => [String(r.id), r]))
  for (const r of ar) if (!bById.has(String(r.id))) out.push(L.addsRule(String(r.id), Number(r.confidence), String(r.kind)))
  for (const r of br) if (!aById.has(String(r.id))) out.push(L.removesRule(String(r.id)))
  for (const r of br) {
    const id = String(r.id)
    const n = aById.get(id)
    if (!n) continue
    if (r.kind !== n.kind) out.push(L.ruleField(id, 'kind', str(r.kind), str(n.kind)))
    const bt = ruleKindWords(Number(r.confidence)), at = ruleKindWords(Number(n.confidence))
    if (bt !== at) out.push(L.ruleType(id, bt, at))
    else if (Number(r.confidence) !== Number(n.confidence)) out.push(L.ruleField(id, 'confidence', str(r.confidence), str(n.confidence)))
    if (!same(r.when, n.when)) {
      const bw = rec(r.when), aw = rec(n.when)
      const keys = [...new Set([...Object.keys(bw), ...Object.keys(aw)])].filter((k) => !same(bw[k], aw[k]))
      out.push(L.ruleConditions(id, conditionLabels(Object.fromEntries(keys.map((k) => [k, 1])))))
    }
  }
  const keptRulesBefore = br.map((r) => String(r.id)).filter((id) => aById.has(id))
  const keptRulesAfter = ar.map((r) => String(r.id)).filter((id) => bById.has(id))
  if (!same(keptRulesBefore, keptRulesAfter)) out.push(L.rulesReordered)

  // model
  const bm = rec(before.model), am = rec(after.model)
  if (bm.enabled !== am.enabled) out.push(L.modelEnabled(bm.enabled, am.enabled))
  if (bm.instruction !== am.instruction) out.push(L.modelInstruction)
  return out
}
